<!--
 * @Description: 开票数据模块（容器）— 申请开票时的类目分组管理
 *               标题 + 新增类目分组 + 多分组循环（Item 子组件）+ 价税合计/最大开票金额汇总
 *               已选数据跨分组排除（globalPreSelectedIds 由本容器统一下发）
 *               v-model:categoryGroups 对外暴露分组数据；validate() 校验
 * @Date: 2026-06-25
-->
<template>
  <div class="modal-section">
    <div class="modal-section-title">
      开票数据
      <BaseButton
        v-if="!isConfirm"
        type="text"
        style="color: #e97132"
        @click="onAddGroup"
        :disabled="!canAddGroup"
      >
        +新增类目分组
      </BaseButton>
    </div>

    <template v-for="(group, idx) in categoryGroups" :key="idx">
      <IndividualCategoryGroupItem
        :groupIndex="idx"
        :formData="formData"
        :itemId="group.itemId"
        :settlementList="group.settlementList"
        :globalPreSelectedIds="allPreSelectedIds"
        :disabled="disabled"
        @update:data="data => onGroupDataUpdate(idx, data)"
        @remove="onRemoveGroup"
      />
    </template>

    <!-- 汇总 -->
    <div class="group-summary">
      <span>
        价税合计：
        <b>{{ formatter.fen2yuan(totalTaxPrice) }}</b>
        元
      </span>
      <span>
        最大开票金额：
        <b>{{ formatter.fen2yuan(maxInvoiceAmount) }}</b>
        元
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { BaseButton, XrkMessage } from 'xrk-components';
import { formatter } from '@/utils';
import type { CategoryGroups } from '@V/invoice/src/data-source/types';
import IndividualCategoryGroupItem from './individual-category-group-item.vue';

withDefaults(
  defineProps<{
    formData: {
      theSellerId?: number | null;
      invoiceType?: number | null;
      payPassagewayCode?: string;
      individualId?: number | null;
    };
    /** 确认步骤（只读，隐藏新增按钮） */
    isConfirm?: boolean;
    /** 下发给子分组的禁用态 */
    disabled?: boolean;
  }>(),
  {
    formData: () => ({}),
    isConfirm: false,
    disabled: false
  }
);

// 分组数据 v-model（增删改回写父级）
const categoryGroups = defineModel<CategoryGroups>('categoryGroups', {
  default: () => [{ itemId: null, itemName: '', settlementList: [] }]
});

/** 所有分组已选ID集合（跨组传递给弹窗做排除） */
const allPreSelectedIds = computed(() => {
  const ids = new Set<number>();
  categoryGroups.value.forEach(g => {
    (g.settlementList || []).forEach((item: any) => {
      ids.add(item.settlementUserTaskId);
    });
  });
  return [...ids];
});

const allSelectedItems = computed(() => {
  const seen = new Set<number>();
  const items: any[] = [];
  categoryGroups.value.forEach(g => {
    (g.settlementList || []).forEach((item: any) => {
      if (!seen.has(item.settlementUserTaskId)) {
        seen.add(item.settlementUserTaskId);
        items.push(item);
      }
    });
  });
  return items;
});

const totalTaxPrice = computed(() => {
  return allSelectedItems.value.reduce(
    (sum, item) => sum + (item.terminalSettlementAmount || 0),
    0
  );
});

const maxInvoiceAmount = computed(() => totalTaxPrice.value);

const canAddGroup = computed(() => {
  const last = categoryGroups.value[categoryGroups.value.length - 1];
  return last && last.settlementList?.length > 0;
});

const onGroupDataUpdate = (
  idx: number,
  data: { itemId: number | null; itemName: string; settlementList: any[] }
) => {
  categoryGroups.value[idx] = { ...categoryGroups.value[idx], ...data };
};

const onRemoveGroup = (idx: number) => {
  categoryGroups.value.splice(idx, 1);
};

const onAddGroup = () => {
  categoryGroups.value.push({
    itemId: null,
    itemName: '',
    settlementList: []
  });
};

/* ---------------- 校验（暴露给父组件） ---------------- */
const validate = () => {
  if (
    !categoryGroups.value.length ||
    !categoryGroups.value.every(g => g.settlementList.length)
  ) {
    XrkMessage.error('请至少选择一个类目分组并选择开票数据');
    return Promise.reject(new Error('请至少选择一个类目分组并选择开票数据'));
  }
  return Promise.resolve();
};

defineExpose({ validate });
</script>

<style lang="scss" scoped>
.modal-section {
  margin-bottom: 20px;
}

.modal-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.group-summary {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fdf6ec;
  border: 1px solid #faecd8;
  border-radius: 4px;
  margin-top: 12px;
  font-size: 14px;
}
</style>
